import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CodeIcon, ShoppingCartIcon, SmartphoneIcon, DatabaseIcon, LinkIcon, ServerIcon, ClipboardCheckIcon, BrainCircuitIcon, BotIcon, TrendingUpIcon, MessageSquareIcon, CheckIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { ServiceCard } from '../components/ServiceCard';
import { CTASection } from '../components/CTASection';
export function Home() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  return <div className="min-h-screen w-full bg-white" data-id="element-207">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-surface to-white py-20 md:py-32" data-id="element-208">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-209">
          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="text-center max-w-4xl mx-auto" data-id="element-210">
            <motion.p variants={itemVariants} className="text-sm font-semibold text-primary mb-4 tracking-wide uppercase" data-id="element-211">
              Accepting new projects by referral only
            </motion.p>

            <motion.h1 variants={itemVariants} className="text-4xl md:text-6xl font-bold text-ink mb-6 leading-tight" data-id="element-212">
              Custom Software Development by Referral
            </motion.h1>

            <motion.p variants={itemVariants} className="text-xl md:text-2xl text-gray-600 mb-10 leading-relaxed" data-id="element-213">
              Magnus Software builds tailored web applications, mobile apps, and
              AI-powered solutions for select clients. We partner with
              businesses through trusted referrals to deliver engineering
              excellence and measurable results.
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-214">
              <Link to="/contact" data-id="element-215">
                <Button variant="primary" size="lg" data-id="element-216">
                  Discuss Your Project
                </Button>
              </Link>
              <Link to="/services" data-id="element-217">
                <Button variant="secondary" size="lg" data-id="element-218">
                  View Our Services
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-12 border-y border-neutral bg-white" data-id="element-219">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-220">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="grid grid-cols-1 md:grid-cols-5 gap-6 text-center" data-id="element-221">
            <div className="flex flex-col items-center" data-id="element-222">
              <CheckIcon className="w-6 h-6 text-primary mb-2" data-id="element-223" />
              <p className="text-sm font-medium text-gray-700" data-id="element-224">
                Custom software engineered for your exact requirements
              </p>
            </div>
            <div className="flex flex-col items-center" data-id="element-225">
              <CheckIcon className="w-6 h-6 text-primary mb-2" data-id="element-226" />
              <p className="text-sm font-medium text-gray-700" data-id="element-227">
                Proven expertise in web, mobile, and enterprise systems
              </p>
            </div>
            <div className="flex flex-col items-center" data-id="element-228">
              <CheckIcon className="w-6 h-6 text-primary mb-2" data-id="element-229" />
              <p className="text-sm font-medium text-gray-700" data-id="element-230">
                AI and automation capabilities that drive efficiency
              </p>
            </div>
            <div className="flex flex-col items-center" data-id="element-231">
              <CheckIcon className="w-6 h-6 text-primary mb-2" data-id="element-232" />
              <p className="text-sm font-medium text-gray-700" data-id="element-233">
                Scalable architecture built for growth
              </p>
            </div>
            <div className="flex flex-col items-center" data-id="element-234">
              <CheckIcon className="w-6 h-6 text-primary mb-2" data-id="element-235" />
              <p className="text-sm font-medium text-gray-700" data-id="element-236">
                Long-term partnership and ongoing support
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white" data-id="element-237">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-238">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16" data-id="element-239">
            <h2 className="text-3xl md:text-4xl font-bold text-ink mb-4" data-id="element-240">
              Comprehensive Software Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-241">
              From web applications to AI automation, we deliver end-to-end
              software development services
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-242">
            <ServiceCard icon={CodeIcon} title="Web Applications" description="Modern, responsive web apps built with cutting-edge frameworks. From dashboards to complex enterprise platforms." delay={0} data-id="element-243" />
            <ServiceCard icon={ShoppingCartIcon} title="E-Commerce Solutions" description="Custom online stores and payment integrations designed for conversion and scalability." delay={0.1} data-id="element-244" />
            <ServiceCard icon={SmartphoneIcon} title="Mobile Applications" description="Native iOS and Android apps that deliver seamless user experiences across devices." delay={0.2} data-id="element-245" />
            <ServiceCard icon={DatabaseIcon} title="Database Solutions" description="Robust data architecture, migrations, and import/export systems for complex datasets." delay={0.3} data-id="element-246" />
            <ServiceCard icon={LinkIcon} title="System Integrations" description="Connect your tools and platforms with custom APIs and middleware solutions." delay={0.4} data-id="element-247" />
            <ServiceCard icon={ServerIcon} title="High Availability Infrastructure" description="Clustering, load balancing, and failover systems that keep your business running 24/7." delay={0.5} data-id="element-248" />
            <ServiceCard icon={ClipboardCheckIcon} title="Project Management" description="Structured delivery with clear milestones, transparent communication, and on-time execution." delay={0.6} data-id="element-249" />
            <ServiceCard icon={BrainCircuitIcon} title="AI & Automation" description="Intelligent assistants, workflow automation, and machine learning solutions that scale your operations." delay={0.7} data-id="element-250" />
          </div>
        </div>
      </section>

      {/* AI Highlight */}
      <section className="py-20 bg-surface" data-id="element-251">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-252">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16" data-id="element-253">
            <h2 className="text-3xl md:text-4xl font-bold text-ink mb-4" data-id="element-254">
              AI-Powered Capabilities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-255">
              Leverage artificial intelligence to automate workflows and scale
              your business
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-256">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0
          }} className="bg-white p-8 rounded-xl shadow-sm" data-id="element-257">
              <div className="w-14 h-14 bg-surface rounded-xl flex items-center justify-center mb-6" data-id="element-258">
                <BotIcon className="w-7 h-7 text-primary" data-id="element-259" />
              </div>
              <h3 className="text-2xl font-bold text-ink mb-3" data-id="element-260">
                AI Assistants & Smart Automation
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-261">
                Deploy intelligent agents that handle routine tasks, provide
                instant answers, and learn from your business processes.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-white p-8 rounded-xl shadow-sm" data-id="element-262">
              <div className="w-14 h-14 bg-surface rounded-xl flex items-center justify-center mb-6" data-id="element-263">
                <TrendingUpIcon className="w-7 h-7 text-primary" data-id="element-264" />
              </div>
              <h3 className="text-2xl font-bold text-ink mb-3" data-id="element-265">
                AI-Powered SEO & Analytics
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-266">
                Optimize your digital presence with AI-driven insights, content
                recommendations, and performance analysis.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-white p-8 rounded-xl shadow-sm" data-id="element-267">
              <div className="w-14 h-14 bg-surface rounded-xl flex items-center justify-center mb-6" data-id="element-268">
                <MessageSquareIcon className="w-7 h-7 text-primary" data-id="element-269" />
              </div>
              <h3 className="text-2xl font-bold text-ink mb-3" data-id="element-270">
                24/7 AI Live Chat
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-271">
                Engage visitors around the clock with conversational AI that
                answers questions and qualifies leads automatically.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What Solution Section */}
      <section className="py-20 bg-white" data-id="element-272">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-273">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="max-w-4xl mx-auto" data-id="element-274">
            <h2 className="text-3xl md:text-4xl font-bold text-ink mb-8 text-center" data-id="element-275">
              What Solution Do You Need?
            </h2>

            <div className="bg-surface rounded-xl p-8 md:p-12" data-id="element-276">
              <p className="text-lg text-gray-700 mb-6 font-medium" data-id="element-277">
                We specialize in:
              </p>
              <ul className="space-y-4" data-id="element-278">
                <li className="flex items-start" data-id="element-279">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-280" />
                  <span className="text-gray-700" data-id="element-281">
                    Custom web application development tailored to your workflow
                  </span>
                </li>
                <li className="flex items-start" data-id="element-282">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-283" />
                  <span className="text-gray-700" data-id="element-284">
                    Mobile apps for iOS and Android with native performance
                  </span>
                </li>
                <li className="flex items-start" data-id="element-285">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-286" />
                  <span className="text-gray-700" data-id="element-287">
                    E-commerce platforms with secure payment processing
                  </span>
                </li>
                <li className="flex items-start" data-id="element-288">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-289" />
                  <span className="text-gray-700" data-id="element-290">
                    Database design, optimization, and migration services
                  </span>
                </li>
                <li className="flex items-start" data-id="element-291">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-292" />
                  <span className="text-gray-700" data-id="element-293">
                    System integrations connecting your existing tools
                  </span>
                </li>
                <li className="flex items-start" data-id="element-294">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-295" />
                  <span className="text-gray-700" data-id="element-296">
                    High availability infrastructure and clustering solutions
                  </span>
                </li>
                <li className="flex items-start" data-id="element-297">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-298" />
                  <span className="text-gray-700" data-id="element-299">
                    AI assistants and intelligent automation
                  </span>
                </li>
                <li className="flex items-start" data-id="element-300">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-301" />
                  <span className="text-gray-700" data-id="element-302">
                    Legacy system modernization and cloud migration
                  </span>
                </li>
                <li className="flex items-start" data-id="element-303">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-304" />
                  <span className="text-gray-700" data-id="element-305">
                    Ongoing maintenance and technical support
                  </span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Project Management Section */}
      <section className="py-20 bg-surface" data-id="element-306">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-307">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="max-w-4xl mx-auto text-center" data-id="element-308">
            <h2 className="text-3xl md:text-4xl font-bold text-ink mb-6" data-id="element-309">
              Structured Delivery, Clear Communication
            </h2>
            <p className="text-lg text-gray-600 mb-12 leading-relaxed" data-id="element-310">
              Every Magnus Software project follows a proven methodology:
              discovery and planning, iterative development with regular
              check-ins, thorough testing and quality assurance, and smooth
              deployment with documentation. You'll always know where your
              project stands, what's next, and when to expect delivery.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12" data-id="element-311">
              <div className="bg-white p-6 rounded-xl" data-id="element-312">
                <h3 className="text-xl font-bold text-ink mb-2" data-id="element-313">
                  Transparent Milestones
                </h3>
                <p className="text-gray-600" data-id="element-314">
                  Progress tracking you can count on
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl" data-id="element-315">
                <h3 className="text-xl font-bold text-ink mb-2" data-id="element-316">
                  Regular Updates
                </h3>
                <p className="text-gray-600" data-id="element-317">
                  Stakeholder communication at every step
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl" data-id="element-318">
                <h3 className="text-xl font-bold text-ink mb-2" data-id="element-319">
                  On-Time Delivery
                </h3>
                <p className="text-gray-600" data-id="element-320">
                  Comprehensive documentation included
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white" data-id="element-321">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-322">
          <CTASection headline="Ready to Build Something Exceptional?" body="Magnus Software accepts new clients by referral to ensure the best fit and maintain our commitment to quality. If a trusted partner recommended us, we'd love to discuss your project." buttonText="Start the Conversation" buttonLink="/contact" variant="dark" data-id="element-323" />
        </div>
      </section>
    </div>;
}